import React, { useEffect, useState } from 'react';

import {
  Backdrop, CircularProgress, Grid, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import _ from 'lodash';

import ContentCard from '../../../components/ContentCard';
import {
  Button, Values,
} from '../../../components/DetailsModal';
import EventCell from '../../../components/EventCell';
import Link from '../../../components/Link';
import { NoPermissionMessage } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import WeekCalendar from '../../../components/WeekCalendar';
import { CalendarEntry } from '../../../components/WeekCalendar/types';
import { getCurrentUserId } from '../../../services/auth';
import { useGetCoursesQuery } from '../../../services/courses';
import {
  useBookTutorialMutation, useGetTutorialsInRangeQuery, useUnbookTutorialMutation,
} from '../../../services/tutorials';
import { Tutorial } from '../../../services/tutorials/types';
import {
  getBaseAttributes, hasTutorialPermissions,
} from '../../../services/tutorials/utils';
import addDays from '../../../utils/common/addDays';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import useUserPermissions from '../../../utils/react/hooks/useUserPermissions';
import { TutorialDetails } from '../types';

import {
  Description, HighlightText, LoadingWrapper,
} from './index.styles';
import { getStudentTutorialState, TutorialUserType } from './utils';

const TODAY = dayjs().startOf('day');

// Earliest date to display tutorials
const MIN_DATE = addDays(TODAY, -7);

// Latest date to display tutorials
const MAX_DATE = addDays(TODAY, 14);

const StudentTutorialsPage = () => {
  const studentId = getCurrentUserId();
  const permissions = useUserPermissions();
  const [hasTutorialAccess, setHasTutorialAccess] = useState<boolean | undefined>(
    permissions ? hasTutorialPermissions(permissions) : undefined,
  );

  const { data: tutorialsInRange, isLoading } = useGetTutorialsInRangeQuery({
    startDatetime: MIN_DATE.valueOf(),
    endDatetime: MAX_DATE.valueOf(),
  });
  const { data: courses } = useGetCoursesQuery({});
  const [bookTutorial,
    { isError: isBookingError,
      isSuccess: isBookingSuccess,
      error: bookingError,
      isLoading: isBookingLoading }] = useBookTutorialMutation();
  const [unbookTutorial,
    { isError: isUnbookingError,
      isSuccess: isUnbookingSuccess,
      error: unbookingError,
      isLoading: isUnbookingLoading }] = useUnbookTutorialMutation();

  const toCalendarEntry = (tutorial: Tutorial): CalendarEntry<TutorialDetails> => {
    const user =
     tutorial.attendees?.some((student) => student.studentId === getCurrentUserId())
       ? TutorialUserType.Attendee
       : TutorialUserType.Student;

    const bookButton: Button = {
      fn: (values?: Values, newValues?: Values) => {
        bookTutorial({
          course: tutorial.course,
          id: tutorial.startDatetimeIdentifier,
          studentId,
          note: newValues?.note,
        });
      },
      label: 'Book',
      color: 'darkPrimary',
      variant: 'contained',
      loading: isBookingLoading,
    };

    const unbookButton: Button = {
      fn: () => unbookTutorial({
        course: tutorial.course,
        id: tutorial.startDatetimeIdentifier,
        studentId,
      }),
      label: 'Unbook',
      color: 'error',
      loading: isUnbookingLoading,
    };

    const joinButton: Button = {
      fn: () => window.open(tutorial.joinLink),
      label: 'Join',
      color: 'success',
    };

    const tutorialState = getStudentTutorialState(
      tutorial, user, bookButton, unbookButton, joinButton);

    const baseAttributes = getBaseAttributes(tutorial, courses?.data ?? []);

    return {
      ...baseAttributes,
      // Only includes note field if tutorial is open
      // Only includes Zoom link if tutorial is imminent
      details: {
        ...baseAttributes.details,
        properties: {
          ..._.pick(tutorial, ['host', 'description']),
          ...(tutorialState.state === 'open'
            && { note: '' }),
          ...(tutorialState.state === 'imminent'
          && { joinLink: tutorial.joinLink }),
        },
        extraFields: {
          ...(tutorialState.state === 'open' && {
            note: {
              label: 'Requested Topic(s)',
              type: 'long',
              editable: true,
            },
          }),
          ...(tutorialState.state === 'imminent' && {
            joinLink: {
              label: 'Zoom Link',
              type: 'text',
              editable: false,
            },
          }),

        },
        state: tutorialState,
      },
    };
  };

  const [tutorials, setTutorials] = useState<CalendarEntry<TutorialDetails>[]>([]);

  useEffect(() => {
    if (tutorialsInRange && courses) {
      setTutorials(tutorialsInRange?.data.map((tutorial: Tutorial) => toCalendarEntry(tutorial)));
    }
  }, [tutorialsInRange, courses, isBookingLoading, isUnbookingLoading]);

  useEffect(() => {
    if (permissions) {
      setHasTutorialAccess(hasTutorialPermissions(permissions));
    }
  }, [permissions]);

  useError(isBookingError, bookingError);
  useError(isUnbookingError, unbookingError);

  useSuccess(isUnbookingSuccess, 'Tutorial Unbooked');
  useSuccess(isBookingSuccess, 'Tutorial Booked');

  if (!permissions) {
    return (
      <SkeletonStack />
    );
  }

  if (hasTutorialAccess === false) {
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <>
      {
        isLoading ? (
          <LoadingWrapper>
            <CircularProgress />
          </LoadingWrapper>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <ContentCard title="About Online Tutorials">
                <Description>
                  <Typography variant="body2">
                    <HighlightText>Online Tutorials</HighlightText> are short consultations
                    delivered via Zoom with
                    only <HighlightText>1 - 3 students</HighlightText> per session.
                    <br />
                    <br />
                    To make the most out of Online Tutorials, we encourage you to use your
                    class resources and <Link href="/videos">Cognito Videos</Link>{' '}
                    to learn the content first, then clarify concepts via Online Tutorials
                    and <Link href="http://cognitotuition.slack.com" target="_blank">Slack</Link> afterwards.
                  </Typography>
                </Description>
              </ContentCard>
            </Grid>
            <Grid item xs={12} md={12}>
              <ContentCard
                variant="outlined"
                title="Book a Tutorial"
              >
                <Typography variant="caption">
                  Sessions are limited
                  to <HighlightText>one per week</HighlightText> and
                  bookings for empty tutorials are locked
                  24 hours before they start.<br />
                </Typography>
                <WeekCalendar
                  data={tutorials}
                  renderContent={(entry) => (
                    <EventCell
                      key={entry.id}
                      startDatetime={entry.startDatetime}
                      duration={entry.details.duration}
                      title={entry.details.title}
                      color={entry.details.color}
                      state={entry.details.state}
                      propertyFields={{
                        ..._.pick(tutorialsInRange?.metadata || {}, [
                          'description',
                          'host',
                        ]),
                        ...entry.details.extraFields,
                      }}
                      propertyValues={entry.details.properties}
                      fetchProperties={entry.details.fetchProperties}
                      showStatus
                    />
                  )}
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                />
              </ContentCard>
            </Grid>
          </Grid>
        )
      }
      {(isBookingLoading || isUnbookingLoading) && (
        <Backdrop open>
          <CircularProgress />
        </Backdrop>
      )}
    </>
  );
};
export default StudentTutorialsPage;
